import { DEFAULT_PHONE } from '../../constants/contact-info';
import { type ChatContactInfo, type PhoneContactInfo } from '../types/site.types';

export const SITE_NAME = 'Ferguson Home';
export const DEFAULT_META_DESCRIPTION =
	'Huge Online Savings on over 500,000 home improvement projects: Shop faucets, sinks, lighting, hardware, fans, appliances and more at Build with Ferguson.';
export const APPENDED_SITE_NAME = ` | ${SITE_NAME}`;

export const DEFAULT_PHONE_INFO: PhoneContactInfo = {
	openRange: null,
	number: DEFAULT_PHONE,
	weekdayHours: {
		startTime: '6:00am',
		endTime: '6:00pm'
	},
	weekendHours: {
		startTime: '6:00am',
		endTime: '4:00pm'
	},
	exceptions: []
};
export const DEFAULT_CHAT_INFO: ChatContactInfo = {
	openRange: null,
	weekdayHours: {
		startTime: '6:00am',
		endTime: '6:00pm'
	},
	weekendHours: {
		startTime: '6:00am',
		endTime: '4:00pm'
	},
	exceptions: []
};

/**
 * Maximum acceptable time for a UI response to a user action is 400ms (Doherty Threshold)
 *
 * @see https://buildcom.github.io/styleguide/?path=/story/docs-best-practices--asynchronous-loading
 */
export const DOHERTY_THRESHOLD = 400;

/**
 * Allowing the page to load before performing an action by waiting 1000ms
 */
export const PAGE_LOAD_DELAY = 1000;

/**
 * No UI response up to 0.1s after user action is acceptable
 *
 * @see https://buildcom.github.io/styleguide/?path=/story/docs-best-practices--asynchronous-loading
 */
export const ACCEPTABLE_DELAY = 100;

// This enum exists in both client and server constants. Make sure to update in both places if needed.

export enum FEATURE_FLAGS {
	A_A_TRIAL = 'a-a-trial',
	AMAZON_PAYPAL_BUTTONS = 'ab-amazon-pay-and-paypal-buttons',
	PAYPAL_PAYMENT = 'paypal-payment',
	AMAZON_PAYMENT = 'amazon-payment',
	APPLE_PAYMENT = 'apple-payment',
	CONTACT_CENTER_ENABLED = 'contact-center-number',
	HIDE_CATEGORY_PAGESIZE = 'hide-category-pagesize',
	FUSION_CATEGORY_SEARCH = 'fusion-category-search',
	FUSION_CATEGORY_POPULARITY_ALGORITHM = 'fusion-category-popularity-algorithm',
	DISABLE_PRECHAT_SURVEY = 'disable-prechat-survey',
	ADDRESS_AUTO_COMPLETE = 'address-auto-complete',
	FUSION_KEYWORD_SEARCH_QUERY_PROFILE = 'fusion-keyword-search-query-profile',
	FUSION_CATEGORY_SEARCH_QUERY_PROFILE = 'fusion-category-search-query-profile',
	KEYWORD_SEARCH_TOP_FACETS = 'keyword-search-top-facets',
	SORT_INSIDE_FACET_DRAWER = 'sort-inside-facet-drawer',
	SAVE_FOR_LATER = 'ab-save-for-later',
	KEYWORD_SEARCH_TAGS = 'keyword-search-tags',
	CATEGORY_SEARCH_TAGS = 'category-search-tags',
	SHOW_SHIPPING_ADDRESS_DELETE_BUTTON = 'ab-show-shipping-address-delete-button',
	ENABLE_REPROCESS_PAYMENT = 'enable-reprocess-payment',
	FACET_GROUP_COLLAPSE_SIZE = 'facet-group-collapse-size',
	HIDE_COMPLETE_ORDER_BUTTON = 'ab-hide-complete-order-button',
	MOBILE_STICKY_COMPLETE_ORDER_BUTTON = 'ab-mobile-sticky-complete-order-button',
	SHOWROOM_ENABLE_ONLINE_APPOINTMENT_BOOKING = 'showroom-enable-online-appointment-booking',
	ANNOUNCEMENT_BANNER = 'announcement-banner',
	SOLR_KEYWORD_SEARCH = 'solr-keyword-search',
	SOLR_COLLECTION_SEARCH = 'solr-collection-search',
	HIDE_IMPORTED_BIDS_PROJECT_PRICING = 'hide-imported-bids-project-pricing',
	COMPLEX_ORDER = 'complex-order',
	NEW_PROJECT_PORTFOLIO = 'project-portfolio',
	NEW_PROJECT_PORTFOLIO_TOGGLE = 'portfolio-enhancement-toggle',
	PROJECTS_INTERVIEW_BOOKING = 'projects-interview-booking',
	FERGUSON_CREDIT = 'ferguson-credit',
	SF_SITE_ID = 'sf-site-id',
	BAZAARVOICE_QA_API = 'bazaar-voice-qa-api',
	AMEX_PAYMENT = 'amex-payment',
	TYPEAHEAD_MOBILE_VIEW = 'typeahead-mobile-view',
	LIVE_CHAT = 'live-chat',
	INTERNAL_CHAT = 'internal-chat',
	ENHANCED_LIVE_CHAT = 'enhanced-external-chat',
	SEARCH_DROPDOWN_RESTYLE = 'search-dropdown-restyle',
	INVENTORY_RESTRICTIONS = 'inventory-restrictions',
	SHOWROOM_APPOINTMENT_BOOKING_STANDALONE_FLOW = 'showroom-appointment-booking-standalone-flow',
	ECOREBATES_WIDGETS = 'eco-rebates-widgets',
	CUSTOM_REVIEWS_V2 = 'custom-reviews-v2',
	DIGITAL_BRANCH_ORDERS = 'digital-branch-orders',
	MANUAL_PROJECT_ATTRIBUTION = 'manual-project-attribution',
	HOMEPAGE_PROJECTS_FIRST = 'homepage-projects-first'
}

export const PAGE_CONTENT_STYLE = 'center-ns mw9-ns bg-theme-white pa2 pa3-ns mt3 mb3';

export const FOCUSABLE_SELECTOR =
	'a,frame,iframe,input:not([type=hidden]):not(:disabled),select:not(:disabled),textarea:not(:disabled),button:not(:disabled),[tabindex]:not([tabindex="-1"])';

export const MAX_NUMBER_OF_COMPARED_PRODUCTS = 4;

export type ZIndices = '1' | '2' | '3' | '4' | '5' | '999' | '9999' | 'max';

/**
 * node-store login form requires a min length of 2, but create account is 6,
 * maybe we can bump this up.
 */
export const MIN_PASSWORD_LENGTH = 2;

/**
 * Minimum password length for new accounts.
 */
export const MIN_NEW_PASSWORD_LENGTH = 10;

/**
 * used for rem calculations
 */
export const BASE_FONT_SIZE_PX = 16;

/**
 * Save to projects notification timeout
 */
export const PROJECTS_NOTIFICATION_TIMEOUT = 5000;

/**
 * Order Number max number
 */
export const JAVA_MAX_INT = 2147483647;

/**
 * Order Number min number
 * Used to prevent old orders from being entered (orders before they were uniquely identified). The backend only
 * supports the return of one order and numbers below 25883 will return an error.
 */
export const MIN_ORDER_NUMBER = 25883;

/**
 * Unicode for a non breaking space.  This can be used in JSX to add a non
 * breaking space.
 */
export const NON_BREAKING_SPACE = '\u00A0';

/**
 * Text Field Debounce Threshold
 */
export const DEBOUNCED_THRESHOLD = 500;

export const HIDE_UNDO_REMOVE_TIMEOUT = 10000; // 10 seconds;

export type HeadingValuesType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/**
 * Popover tooltip timeout
 */
export const TOOLTIP_DISPLAY_TIME = 3000;

export const CATEGORY_PLP_PAGE_NAME = 'categoryProductDrop:browse';

export const OVERLAY_MARKER = 'overlay-header-marker';

/**
 * Event names for custom javascript events (not analytics events)
 */
export enum EVENTS {
	COMPLETED_PAGE_TRACK = 'completed-page-track'
}

export enum AUTH_REDIRECT_REASON {
	SESSION_EXPIRED = 'session_expired'
}

export const PAGE_CONTENT_ELEMENT = 'page-content';
